"use client";
import useTranslate from "@app/_hooks/useTranslate";
import { CheckIcon } from "lucide-react";

import { isProductOversellable } from "@app/_components/Stock/Stock.utils";
import Link from "@components/Link";
import { Quantity } from "@components/ngg";
import { Button } from "@components/ui/Button";
import { useCartActions, useCartState } from "@context/cartContext";
import useSignUp from "@hooks/useSignUp";
import type { ProductPageInfoFragment } from "@server/gql/graphql";
import { getStockWatch } from "@server/requests/checkout/stockWatch";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, m } from "framer-motion";
import type { FormEvent, PropsWithChildren } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Product, Variant } from "types/product";

import { cn } from "@/lib/utils";
import WishlistPDPToggle from "@features/account/components/wishlist/WishlistPDPToggle";
import LoginButton from "@features/auth/components/LoginButton";
import useAuth from "@features/auth/hooks/useAuth";

export default function ProductPageCart({
  variantIds,
  lightsource,
  product,
  variant,
  className,
  locale,
  children,
}: PropsWithChildren<{
  variantIds: string[];
  lightsource?: Variant | null;
  product: Product;
  variant?: Variant;
  className?: string;
  locale?: string;
  madeToOrderBlock?: ProductPageInfoFragment["madeToOrderInfo"];
}>) {
  const t = useTranslate();
  const { user } = useAuth();
  const [quantity, setQuantity] = useState(1);
  const { cart, loading, error } = useCartState();
  const { addCartItem } = useCartActions();
  const [recentlyAdded, setRecentlyAdded] = useState<boolean>(false);
  const [optimisticallyAdded, setOptimisticallyAdded] =
    useState<boolean>(false);
  const [addedToCart, setAddedToCart] = useState(false);

  /**
   * We won't have stock data if item is oversellable and we don't care
   * about how many items are in the cart
   */
  const { data: stockData } = useQuery({
    queryKey: ["stockWatch", product.id],
    queryFn: () =>
      getStockWatch({
        items: variantIds.map((partNo) => ({ partNo, quantity: 1000 })),
      }),
    enabled: !isProductOversellable(product),
  });

  const isDisableAddQuantity = useMemo(() => {
    if (!stockData) {
      return false;
    }

    const cartItem = cart?.items.find((item) => item.partNo === variant?.id);
    const stockWatch = stockData?.find((data) => data.partNo === variant?.id);

    if (!stockWatch) {
      return false;
    }

    const currentQuantity = (cartItem?.quantity ?? 0) + quantity;

    return currentQuantity >= stockWatch.currentOnHand;
  }, [cart?.items, quantity, variant, stockData]);

  const isDisabled = useMemo(() => {
    if (!variant?.id) {
      return true;
    }
    if (!variant.articleStatus) {
      return false;
    }

    const cartItem = cart?.items.find((item) => item.partNo === variant?.id);

    if (!cartItem || !stockData) {
      return false;
    }

    const stockWatch = stockData.find((d) => d.partNo === variant?.id);

    if (!stockWatch) {
      return false;
    }

    return cartItem.quantity >= stockWatch.currentOnHand;
  }, [stockData, cart?.items, variant]);

  const handleDecreaseQuantity = () =>
    setQuantity((prev) => Math.max(1, prev - 1));

  const handleIncreaseQuantity = () => {
    if (isDisableAddQuantity) {
      return;
    }

    setQuantity((prev) => prev + 1);
  };

  useEffect(() => {
    setAddedToCart(false);
  }, [variant]);

  const {
    active,
    toggleNotification,
    loading: loadingNotification,
  } = useSignUp({
    productId: product.id,
    variantId: variant?.id,
  });

  const shouldBeMonitored = useMemo(() => {
    if (!variant?.stockStatus) return true;
    if (["SoldOut", "OutOfStock"].includes(variant.stockStatus)) return true;
    if (
      variant.stockStatus === "Incoming" &&
      variant.articleStatus === "stock_item"
    )
      return true;
    return false;
  }, [variant]);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      if (!variant?.id) {
        return;
      }

      setOptimisticallyAdded(true);

      try {
        await addCartItem(
          [
            {
              quantity,
              item: variant,
              product,
            },
            ...(lightsource
              ? [
                  {
                    item: lightsource,
                    quantity: (variant.lightsourceQuantity ?? 1) * quantity,
                  },
                ]
              : []),
          ],
          true,
        );

        setAddedToCart(true);
        setRecentlyAdded(true);
        setOptimisticallyAdded(false);

        setTimeout(() => setRecentlyAdded(false), 3000);
      } catch (error) {
        console.error(error);
        setOptimisticallyAdded(false);
      }
    },
    [addCartItem, quantity, variant, product, lightsource],
  );

  return (
    <div className={cn("grid grid-cols-2 gap-y-2", className)}>
      <form
        onSubmit={handleSubmit}
        className={cn(
          "col-span-2 grid gap-2",
          !shouldBeMonitored && "grid-cols-[min-content_auto_min-content]",
        )}
      >
        {shouldBeMonitored ? (
          <div className="flex gap-2">
            {user ? (
              <Button
                type="button"
                isLoading={loadingNotification}
                disabled={loadingNotification}
                onClick={toggleNotification}
                className={cn(
                  "btn-primary col-span-3 w-full",
                  active && "border-0 bg-green-600 text-white",
                )}
              >
                {active ? t("removeNotification") : t("monitorProduct")}
              </Button>
            ) : (
              <LoginButton
                variant="default"
                className="col-span-3 w-full"
                redirectOnSuccess={false}
              >
                {t("monitorProduct")}
              </LoginButton>
            )}
            <WishlistPDPToggle
              productId={product.productCode}
              variantId={variant?.id ?? ""}
            />
          </div>
        ) : (
          <>
            <Quantity
              readOnly
              onDecrease={handleDecreaseQuantity}
              onIncrease={handleIncreaseQuantity}
              value={quantity}
              classNames={{
                wrapper: "border-0 h-10 lg:h-12",
                input:
                  "border-black border-y  text-base min-w-0 w-10 lg:w-12 -outline-offset-2 action-focus rounded-none font-normal",
                decreaseButton:
                  "action-focus border-black border basis-10 lg:basis-12 -outline-offset-2 w-10 lg:w-12 flex justify-center items-center",
                increaseButton: cn([
                  "action-focus flex w-10 basis-10 items-center justify-center border border-black -outline-offset-2 lg:w-12 lg:basis-12",
                  isDisableAddQuantity && "cursor-not-allowed opacity-50",
                ]),
              }}
            />
            <Button
              type="submit"
              disabled={!variant || isDisabled}
              isLoading={!optimisticallyAdded && loading.addCartItem}
              variant="primary"
              className={cn(
                (recentlyAdded || optimisticallyAdded) &&
                  "border-green pointer-events-none bg-green-600",
                loading.addCartItem && "pointer-events-none",
              )}
              data-testid="PDP-button-Add-to-Cart"
            >
              {recentlyAdded || optimisticallyAdded ? (
                <CheckIcon strokeWidth={1} className="size-5" />
              ) : (
                <span>{t("addToCart")}</span>
              )}
            </Button>
            <WishlistPDPToggle
              productId={product.productCode}
              variantId={variant?.id ?? ""}
            />
            {error.addCartItem ? (
              <p className="text-error col-span-3 mt-1 text-sm md:col-span-1 md:col-start-2 md:pl-4">
                {t("errorAddToCart")}
              </p>
            ) : null}
            <AnimatePresence>
              {addedToCart && (
                <m.div
                  className="col-span-full overflow-hidden"
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                >
                  <Button asChild>
                    <Link
                      locale={locale}
                      className="min-h-10 w-full bg-black p-2 text-base text-white lg:min-h-12 lg:text-lg"
                      href="/checkout"
                    >
                      {t("goToCheckout")}
                    </Link>
                  </Button>
                </m.div>
              )}
            </AnimatePresence>
          </>
        )}
      </form>
      {children}
    </div>
  );
}
